import type { AIDifficulty } from '../../shared/ai/types';
import { SHIP_STATS } from '../../shared/constants';
import type {
  FleetPurchase,
  PlayerId,
  SolarSystemMap,
} from '../../shared/types/domain';
import { isShipFleetPurchase } from '../../shared/types/domain';
import { shouldPresentToast } from '../messages/notification-policy';
import type { UIEvent } from '../ui/events';
import type { AstrogationActionDeps } from './astrogation-actions';
import type { CombatActionDeps } from './combat-actions';
import {
  type CommandRouterDeps,
  type CommandRouterSessionRead,
  dispatchGameCommand,
} from './command-router';
import { type GameCommand, keyboardActionToCommand } from './commands';
import { type InputEvent, interpretInput } from './input-events';
import { deriveInteractionMode } from './interaction-fsm';
import type { KeyboardAction } from './keyboard';
import {
  beginArchivedReplayFromMain,
  beginJoinGameFromMain,
  beginSpectateGameFromMain,
  type MainNetworkDeps,
  startLocalGameFromMain,
} from './main-session-network';
import type { OrdnanceActionDeps } from './ordnance-actions';
import type { ReplayController } from './replay-controller';
import type { SessionApi } from './session-api';
import type { ClientSession } from './session-model';
import { resolveUIEventPlan } from './ui-event-router';

type MainInteractionSession = Pick<
  ClientSession,
  | 'gameStateSignal'
  | 'isLocalGame'
  | 'logisticsStateSignal'
  | 'onboardingEntry'
  | 'planningState'
  | 'playerId'
  | 'stateSignal'
  | 'trainingMovementFeedback'
  | 'transport'
>;

type MainInteractionUI = CommandRouterDeps['ui'] & {
  overlay: {
    showToast: (message: string, type: 'error' | 'info' | 'success') => void;
  };
  showFleetWaiting: () => void;
  toggleHelpOverlay: () => void;
};

type MainInteractionCamera = {
  cycleShip: (direction: 1 | -1) => void;
  focusNearestEnemy: () => void;
  focusOwnFleet: () => void;
};

type MainInteractionHud = {
  updateSoundButton: () => void;
};

type MainInteractionReplay = Pick<
  ReplayController,
  | 'controlsSignal'
  | 'selectMatch'
  | 'toggleReplay'
  | 'exitReplay'
  | 'togglePlay'
  | 'stepReplay'
  | 'cycleSpeed'
>;

type MainInteractionDeps = {
  canvas: Pick<HTMLCanvasElement, 'clientWidth' | 'clientHeight'>;
  map: SolarSystemMap;
  ctx: MainInteractionSession;
  actionDeps: {
    astrogationDeps: AstrogationActionDeps;
    combatDeps: CombatActionDeps;
    ordnanceDeps: OrdnanceActionDeps;
  };
  ui: MainInteractionUI;
  renderer: CommandRouterDeps['renderer'];
  camera: MainInteractionCamera;
  hud: MainInteractionHud;
  replayController: MainInteractionReplay;
  sessionApi: Pick<
    SessionApi,
    | 'createGame'
    | 'startQuickMatch'
    | 'cancelQuickMatch'
    | 'acceptOfficialBotMatch'
  >;
  mainNetworkDeps: MainNetworkDeps;
  setAIDifficulty: (difficulty: AIDifficulty) => void;
  resetTutorial: () => void;
  onTutorialGameplayAction: (action: string) => void;
  exitToMenu: () => void;
  trackEvent: (event: string, props?: Record<string, unknown>) => void;
};

export type MainInteractionController = {
  dispatch: (cmd: GameCommand) => void;
  handleInput: (event: InputEvent) => void;
  handleKeyboardAction: (action: KeyboardAction) => void;
  handleUIEvent: (event: UIEvent) => void;
  joinGame: (code: string, playerToken?: string | null) => void;
  spectateGame: (code: string) => void;
  viewArchivedReplay: (code: string, gameId: string) => void;
  toggleHelp: () => void;
  showToast: (message: string, type?: 'error' | 'info' | 'success') => void;
};

const createCommandSessionRead = (
  ctx: MainInteractionSession,
): CommandRouterSessionRead => ({
  getState: () => ctx.stateSignal.peek(),
  getPlayerId: () => ctx.playerId as PlayerId,
  getGameState: () => ctx.gameStateSignal.peek(),
  getTransport: () => ctx.transport,
  getLogisticsState: () => ctx.logisticsStateSignal.peek(),
  planningState: ctx.planningState,
});

export const createMainInteractionController = (
  deps: MainInteractionDeps,
): MainInteractionController => {
  let comprehensionGameId: string | null = null;
  const firstTurnActions = new Set<string>();

  const trackFirstTurnAction = (action: string): void => {
    const gameState = deps.ctx.gameStateSignal.peek();

    if (!gameState || gameState.turnNumber !== 1) {
      return;
    }

    const gameId = String(gameState.gameId);
    if (gameId !== comprehensionGameId) {
      comprehensionGameId = gameId;
      firstTurnActions.clear();
    }

    if (firstTurnActions.has(action)) {
      return;
    }

    firstTurnActions.add(action);
    deps.trackEvent('first_turn_action', {
      action,
      scenario: gameState.scenario,
      mode: deps.ctx.isLocalGame ? 'local' : 'multiplayer',
      ...(deps.ctx.onboardingEntry ? { entry: deps.ctx.onboardingEntry } : {}),
    });
    deps.onTutorialGameplayAction(action);
  };

  const sendFleetReady = (purchases: FleetPurchase[]) => {
    const gameState = deps.ctx.gameStateSignal.peek();
    if (
      !gameState ||
      deps.ctx.stateSignal.peek() !== 'playing_fleetBuilding' ||
      !deps.ctx.transport
    ) {
      return;
    }

    const shipTypes: Record<string, number> = {};
    let totalCost = 0;
    for (const p of purchases) {
      if (isShipFleetPurchase(p)) {
        shipTypes[p.shipType] = (shipTypes[p.shipType] ?? 0) + 1;
        totalCost += SHIP_STATS[p.shipType].cost;
      }
    }
    deps.trackEvent('fleet_ready_submitted', {
      scenario: gameState.scenario,
      shipCount: purchases.filter(isShipFleetPurchase).length,
      totalCost,
      shipTypes,
    });

    deps.ctx.transport.submitFleetReady(purchases);
    if (!deps.ctx.isLocalGame) {
      deps.ui.showFleetWaiting();
    }
  };

  const sendSurrender = (shipIds: string[]) => {
    const gameState = deps.ctx.gameStateSignal.peek();
    if (!gameState || !deps.ctx.transport) return;
    deps.trackEvent('surrender_submitted', {
      turn: gameState.turnNumber,
      scenario: gameState.scenario,
      mode: deps.ctx.isLocalGame ? 'local' : 'multiplayer',
    });
    deps.ctx.transport.submitSurrender(shipIds);
  };

  const sendRematch = () => {
    deps.ctx.transport?.requestRematch();
  };

  const toggleHelp = () => {
    deps.ui.toggleHelpOverlay();
  };

  const commandSession = createCommandSessionRead(deps.ctx);

  const commandRouterDeps: CommandRouterDeps = {
    ctx: commandSession,
    astrogationDeps: deps.actionDeps.astrogationDeps,
    combatDeps: deps.actionDeps.combatDeps,
    ordnanceDeps: deps.actionDeps.ordnanceDeps,
    ui: { log: deps.ui.log },
    renderer: deps.renderer,
    getCanvasCenter: () => ({
      x: deps.canvas.clientWidth / 2,
      y: deps.canvas.clientHeight / 2,
    }),
    cycleShip: (direction) => deps.camera.cycleShip(direction as 1 | -1),
    focusNearestEnemy: () => deps.camera.focusNearestEnemy(),
    focusOwnFleet: () => deps.camera.focusOwnFleet(),
    sendFleetReady: (purchases) => sendFleetReady(purchases),
    sendSurrender: (shipIds) => sendSurrender(shipIds),
    sendRematch: () => sendRematch(),
    exitToMenu: () => deps.exitToMenu(),
    toggleHelp: () => toggleHelp(),
    updateSoundButton: () => deps.hud.updateSoundButton(),
  };

  const dispatch = (cmd: GameCommand) => {
    const gameStateBefore = deps.ctx.gameStateSignal.peek();
    const clientStateBefore = deps.ctx.stateSignal.peek();
    const selectedShipBefore = deps.ctx.planningState.selectedShipId;
    const burnShipId =
      cmd.type === 'setBurnDirection'
        ? (cmd.shipId ?? selectedShipBefore)
        : selectedShipBefore;
    const burnBefore = burnShipId
      ? deps.ctx.planningState.burns.get(burnShipId)
      : undefined;

    dispatchGameCommand(commandRouterDeps, cmd);

    if (
      !gameStateBefore ||
      gameStateBefore.turnNumber !== 1 ||
      !clientStateBefore.startsWith('playing_')
    ) {
      return;
    }

    switch (cmd.type) {
      case 'selectShip': {
        const ship = gameStateBefore.ships.find(
          (candidate) => candidate.id === cmd.shipId,
        );
        if (
          ship?.owner === (deps.ctx.playerId as PlayerId) &&
          deps.ctx.planningState.selectedShipId === cmd.shipId
        ) {
          trackFirstTurnAction('ship_selected');
        }
        return;
      }
      case 'setBurnDirection':
        if (
          cmd.direction !== null &&
          burnShipId &&
          deps.ctx.planningState.burns.get(burnShipId) !== burnBefore
        ) {
          trackFirstTurnAction('burn_planned');
        }
        return;
      case 'undoBurn':
        if (
          selectedShipBefore &&
          burnBefore !== undefined &&
          deps.ctx.planningState.burns.get(selectedShipBefore) === undefined
        ) {
          trackFirstTurnAction('undo_used');
        }
        return;
      case 'toggleHelp':
        trackFirstTurnAction('help_opened');
        return;
      case 'confirmOrders':
        if (clientStateBefore === 'playing_astrogation' && deps.ctx.transport) {
          trackFirstTurnAction('orders_confirmed');
        }
        return;
      default:
        return;
    }
  };

  const handleInput = (event: InputEvent) => {
    const interactionMode = deriveInteractionMode(deps.ctx.stateSignal.peek());
    if (interactionMode === 'animating') {
      return;
    }

    const commands = interpretInput(
      event,
      deps.ctx.gameStateSignal.peek(),
      interactionMode,
      deps.map,
      deps.ctx.playerId as PlayerId,
      deps.ctx.planningState,
    );

    if (!commands) return;
    for (const cmd of commands) {
      dispatch(cmd);
    }
  };

  const handleKeyboardAction = (action: KeyboardAction) => {
    const cmd = keyboardActionToCommand(action);

    if (cmd) {
      dispatch(cmd);
    }
  };

  const joinGame = (code: string, playerToken: string | null = null) => {
    beginJoinGameFromMain(deps.mainNetworkDeps, code, playerToken);
  };

  const spectateGame = (code: string) => {
    deps.ctx.onboardingEntry = null;
    beginSpectateGameFromMain(deps.mainNetworkDeps, code);
  };

  const viewArchivedReplay = (code: string, gameId: string) => {
    deps.ctx.onboardingEntry = null;
    beginArchivedReplayFromMain(deps.mainNetworkDeps, code, gameId);
  };

  const handleUIEvent = (event: UIEvent) => {
    const plan = resolveUIEventPlan(event);

    switch (plan.kind) {
      case 'quickMatch':
        deps.ctx.onboardingEntry = null;
        void deps.sessionApi.startQuickMatch();
        return;
      case 'cancelQuickMatch': {
        deps.sessionApi.cancelQuickMatch();
        // `cancelQuickMatch` only clears an active quick-match ticket. Hosts
        // waiting for a guest, join/spectate handshakes, and post-match
        // quick-match WebSocket opens still need a full session teardown.
        if (deps.ctx.stateSignal.peek() !== 'menu') {
          deps.exitToMenu();
        }
        return;
      }
      case 'acceptOfficialBotMatch': {
        deps.ctx.onboardingEntry = null;
        void deps.sessionApi.acceptOfficialBotMatch();
        return;
      }
      case 'createGame':
        deps.ctx.onboardingEntry = null;
        // Emit before the round-trip so we capture the scenario even if
        // the /create request fails or the user bails out of the waiting
        // room; `ai_game_started` / `match_created` already cover the
        // commit side of the funnel, this just records intent.
        deps.trackEvent('scenario_selected', {
          scenario: plan.scenario,
          from: 'private',
        });
        deps.sessionApi.createGame(plan.scenario);
        return;
      case 'startSinglePlayer':
        deps.ctx.trainingMovementFeedback = null;
        deps.ctx.onboardingEntry = plan.training
          ? 'training'
          : deps.ctx.onboardingEntry === 'post_training'
            ? 'post_training'
            : null;
        deps.trackEvent('scenario_selected', {
          scenario: plan.scenario,
          from: 'ai',
          difficulty: plan.difficulty,
          ...(deps.ctx.onboardingEntry
            ? { entry: deps.ctx.onboardingEntry }
            : {}),
        });
        if (plan.training) {
          deps.resetTutorial();
        }
        deps.setAIDifficulty(plan.difficulty);
        if (plan.training) {
          startLocalGameFromMain(deps.mainNetworkDeps, plan.scenario, {
            title: 'Training Flight',
            description:
              'A guided Easy Bi-Planetary mission. Follow the prompts to plot burns and land on the enemy world before the AI.',
          });
        } else {
          startLocalGameFromMain(deps.mainNetworkDeps, plan.scenario);
        }
        return;
      case 'joinGame':
        deps.ctx.onboardingEntry = null;
        joinGame(plan.code, plan.playerToken);
        return;
      case 'command':
        // When the top-bar × is clicked during replay playback, route it
        // to exitReplay (→ /matches) rather than exitToMenu (→ /). The
        // replay bar no longer has its own EXIT button.
        if (
          plan.command.type === 'exitToMenu' &&
          deps.replayController.controlsSignal.peek().active
        ) {
          deps.replayController.exitReplay();
          return;
        }
        dispatch(plan.command);
        return;
      case 'selectReplayMatch':
        deps.replayController.selectMatch(plan.direction);
        return;
      case 'toggleReplay':
        void deps.replayController.toggleReplay();
        return;
      case 'exitReplay':
        deps.replayController.exitReplay();
        return;
      case 'replayPlayPause':
        deps.replayController.togglePlay();
        return;
      case 'replayNav':
        deps.replayController.stepReplay(plan.direction);
        return;
      case 'replayCycleSpeed':
        deps.replayController.cycleSpeed();
        return;
      case 'sendChat':
        deps.ctx.transport?.sendChat(plan.text);
        return;
      case 'trackOnly':
        deps.trackEvent(plan.event);
        return;
    }
  };

  const showToast = (
    message: string,
    type: 'error' | 'info' | 'success' = 'info',
  ) => {
    if (!shouldPresentToast(type, deps.ctx.onboardingEntry === 'training')) {
      return;
    }
    deps.ui.overlay.showToast(message, type);
  };

  return {
    dispatch,
    handleInput,
    handleKeyboardAction,
    handleUIEvent,
    joinGame,
    spectateGame,
    viewArchivedReplay,
    toggleHelp,
    showToast,
  };
};
